import React, { useState } from "react";
import { ExternalLink, Bookmark, X, Info, Flame, Radio } from "lucide-react";
import { TID } from "@/lib/testIds";
import PlatformIcon from "@/components/PlatformIcon";
import { useEntitlements } from "@/lib/useEntitlements";
import { LockedButton, PendingButton } from "@/components/UpgradeLock";
import { funnelStageLabel, funnelStageBadgeClass, isPainPost } from "@/lib/funnelStage";

// "2 yrs ago" style relative time.
function timeAgo(d) {
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h} hr${h > 1 ? "s" : ""} ago`;
  const days = Math.floor(h / 24); if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  const mo = Math.floor(days / 30); if (mo < 12) return `${mo} mo ago`;
  const y = Math.floor(days / 365); return `${y} yr${y > 1 ? "s" : ""} ago`;
}

function DateTip({ date }) {
  const full = date.toLocaleString(undefined, { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <span className="relative inline-flex items-center group/dt" tabIndex={0}>
      <Info className="w-3 h-3 text-lm-text3 cursor-help hover:text-lm-primary transition-colors" />
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-30 w-max rounded-md bg-[color:var(--lm-dark)] text-white text-[11px] px-2 py-1 opacity-0 invisible group-hover/dt:opacity-100 group-hover/dt:visible group-focus/dt:opacity-100 group-focus/dt:visible transition-opacity">
        {full}
      </span>
    </span>
  );
}

const PLATFORM_LABEL = {
  twitter: "X",
  reddit: "Reddit",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  quora: "Quora",
  web: "Web",
  news: "News",
  facebook: "Facebook",
  instagram: "Instagram",
  forums: "Forums",
};


// Highlight the matched-keyword words inside the post text.
function highlight(text, keyword) {
  if (!keyword || !text) return text;
  const words = keyword
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!words.length) return text;
  const re = new RegExp(`(${words.join("|")})`, "gi");
  return text.split(re).map((part, i) =>
    words.some((w) => new RegExp(`^${w}$`, "i").test(part)) ? (
      <mark key={i} className="bg-lm-tint2 text-lm-primary rounded px-0.5 font-medium">{part}</mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

export default function LeadCard({ result, onSave, onDismiss, saved = false, saving = false }) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDismiss, setConfirmDismiss] = useState(false);
  const { loaded: entLoaded, canViewSource, canSave } = useEntitlements();
  const platform = PLATFORM_LABEL[result.platform] || result.platform;
  const score = Math.round(result.opportunity_score || 0);
  const stage = result.funnel_stage;
  const isHot = result.urgency === "high";
  const isPain = isPainPost(result.post_kind);
  const posted = result.posted_at ? new Date(result.posted_at) : null;
  const firstSeen = result.first_seen_at ? new Date(result.first_seen_at) : null;
  const content = (result.content || "").trim();
  const preview = content.length > 180 && !expanded ? content.slice(0, 180) + "…" : content;

  return (
    <div
      data-testid={TID.leadCard}
      className="bg-lm-surface border border-lm-border rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[2px] flex flex-col gap-3 relative"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lm-tint text-lm-text2 text-xs font-medium">
              <PlatformIcon platform={result.platform} className="w-3.5 h-3.5" />
              {platform}
            </span>
            {result.is_new_today && (
              <span
                title="Discovered in your latest scan — never shown before"
                className="px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider font-semibold bg-emerald-100 text-emerald-700"
              >
                New
              </span>
            )}
            {stage && (
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider font-semibold ${funnelStageBadgeClass(stage)}`}>
                {funnelStageLabel(stage)}
              </span>
            )}
            {isHot && (
              <span
                title="Urgent — emergency/deadline language in the post itself"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider font-semibold bg-red-100 text-red-700"
              >
                <Flame className="w-3 h-3" /> Hot
              </span>
            )}
            {isPain && (
              <span className="px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider font-semibold bg-amber-100 text-amber-700">
                {result.post_kind}
              </span>
            )}
            {result.is_broadcast && (
              <span
                title="Content/broadcast — not a 1:1 conversation you can reply to"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider font-semibold bg-lm-tint text-lm-text3"
              >
                <Radio className="w-3 h-3" /> Broadcast
              </span>
            )}
            {posted ? (
              <span className="inline-flex items-center gap-1 text-xs font-mono">
                <span className={Date.now() - posted.getTime() > 365 * 864e5 ? "text-amber-600 font-medium" : "text-lm-text3"}>
                  {timeAgo(posted)}
                </span>
                <DateTip date={posted} />
              </span>
            ) : firstSeen ? (
              // This platform/post carries no reliable publication date (doc 07)
              // — never fake one. Show when WE found it instead of hiding it.
              <span
                data-testid="platform-no-date-caveat"
                title="No reliable post date from this platform — shown by when we found it instead."
                className="inline-flex items-center gap-1 text-xs font-mono text-lm-text3"
              >
                found {timeAgo(firstSeen)}
                <DateTip date={firstSeen} />
              </span>
            ) : null}
          </div>
          <div className="text-sm font-medium text-lm-text truncate" title={result.author}>
            {result.author || "unknown author"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-lm-tint2 text-lm-primary font-mono font-bold text-lg leading-none">
            {score}
          </div>
          <div className="text-[10px] text-lm-text3 font-mono uppercase tracking-widest">score</div>
        </div>
      </div>

      {result.title && (
        <div className="text-sm font-semibold text-lm-text -mt-1">{highlight(result.title, result.matched_keyword)}</div>
      )}
      <div className="text-sm text-lm-text2 leading-relaxed whitespace-pre-wrap">
        {highlight(preview, result.matched_keyword)}
        {content.length > 180 && (
          <button
            className="ml-2 text-lm-primary font-medium hover:underline text-xs"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "show less" : "show more"}
          </button>
        )}
      </div>

      {result.matched_keyword && (
        <div className="text-xs text-lm-text3">
          Matched keyword: <span className="font-mono text-lm-text2">{result.matched_keyword}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-auto pt-1">
        {!entLoaded ? (
          <PendingButton label="Source" size="md" data-testid="lead-source-pending" />
        ) : canViewSource ? (
          <a
            data-testid={TID.leadOpenLinkBtn}
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-lm-border bg-lm-surface hover:bg-lm-tint text-sm font-medium"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open
          </a>
        ) : (
          <LockedButton label="Source" size="md" data-testid="lead-source-locked" title="Upgrade to open the original post" />
        )}
        {!entLoaded ? (
          <PendingButton label="Save" size="md" data-testid="lead-save-pending" />
        ) : canSave ? (
          <button
            data-testid={TID.leadSaveBtn}
            onClick={onSave}
            disabled={saving || saved}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${
              saved ? "bg-lm-primary/10 text-lm-primary cursor-default" : "bg-lm-primary text-white hover:bg-lm-primaryHover"
            } disabled:opacity-60`}
          >
            <Bookmark className="w-3.5 h-3.5" fill={saved ? "currentColor" : "none"} />
            {saved ? "Saved" : saving ? "Saving…" : "Save lead"}
          </button>
        ) : (
          <LockedButton label="Save" size="md" data-testid="lead-save-locked" title="Upgrade to save leads" />
        )}
        {onDismiss && !saved && (
          confirmDismiss ? (
            <div className="ml-auto flex items-center gap-1.5 text-xs">
              <span className="text-lm-text3">Remove?</span>
              <button onClick={onDismiss} className="px-2.5 py-1.5 rounded-md bg-red-600 text-white font-medium hover:bg-red-700">Yes</button>
              <button onClick={() => setConfirmDismiss(false)} className="px-2.5 py-1.5 rounded-md text-lm-text2 hover:bg-lm-tint font-medium">No</button>
            </div>
          ) : (
            <button
              data-testid={TID.leadDismissBtn}
              onClick={() => setConfirmDismiss(true)}
              className="inline-flex items-center gap-1.5 ml-auto px-2 py-2 rounded-lg text-sm text-lm-text3 hover:text-red-600 hover:bg-red-50 transition-colors"
              aria-label="Dismiss"
              title="Remove this lead"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )
        )}
      </div>
    </div>
  );
}
